import React, { useState, useEffect, memo, useMemo, useCallback, useContext } from 'react';
import { Button } from '@material-ui/core';
import { getFormattedUtm } from '../../shared/helpers/handleUtm'
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import PlanDetailLane from '../../components/PlanDetailLane';
import usePlan from '../../components/PlanContext';
import FaqSection from '../../components/FaqSection';
import PlanProceduresList from '../../components/PlanProceduresList';
import SwitchButton from '../../components/SwitchButton'
import AnimatedToggler from '../../components/AnimatedToggler';
import { getByCode } from '../../shared/services/plans';
import { useImagesFluid } from '../../hooks/useImages';
import useBreakpoints from '../../hooks/useBreakpoints';
import Layout from 'components/Layout'
import SEO from 'components/Seo'
import * as Styled from './styled';
import { PJ, PF } from '../../shared/constants/paymentsFormats';
import HowToUseList from '../../shared/constants/howToUse';
import DentalServices from '../../shared/constants/services';
import { formatToBRL } from 'brazilian-values';
import { getPlanFilter } from '../../shared/services/plans'
import parseToBRL from '../../shared/utils/parseToBRL'
import { debounce } from 'lodash';
import Banner from '../../components/SecondaryBanner'
import BannerTitle from '../../components/BannerTitle';

const domain = process.env.SELF_SERVICE_URL

const PlanDetailsTemplate = () => {
  const [planDetail, plaDetailActions] = usePlan();
  const { isMobile, isDesk } = useBreakpoints()
  const [isShowingOrthodontics, toggleOrthodontics] = useState(planDetail.hasOrthodontics)
  const [count, setCount] = useState(() => planDetail.isPj ? planDetail.numberOfLives : planDetail.numberOfLives - 1)
  const rangeCount = useMemo(() => planDetail.isPj ? { min: 2, max: 99 } : { min: 0, max: 28 }, [count])
  const [firstEnter, setFirstEnter] = useState(true)
  const [contractLink, setContractLink] = useState('')

  const [plansType, setPlansType] = useState([
    { value: 'anual', label: 'Anual', selected: true },
    { value: 'mensal', label: 'Mensal', selected: false },
  ])

  const [selectedPlan, setSelectedPlan] = useState(planDetail)
  const PAYMENTS_FORMATS = useMemo(() => planDetail.isPj || plansType[1].selected ? PJ : PF, [planDetail, plansType])
  const totalCount = useMemo(() => planDetail.isPj ? count : count + 1, [count])
  const totalValue = useMemo(() => {
    if (planDetail.isPj || totalCount >= 6)
      return totalCount * selectedPlan.value
    return selectedPlan.value;
  }, [selectedPlan.value, selectedPlan, planDetail.isPj])

  const mountContractLink = (code, countLifes) => {
    const numberOfLives = `&numberOfLives=${countLifes}`
    return `${domain}/?planCode=${code}${numberOfLives}${getFormattedUtm()}&origin=institucional&pathOrigin=detalhes-plano`
  }

  useEffect(() => {
    if (firstEnter) {
      getByCodePlan(0, totalCount);
    } else {
      debouncePlanCallback({
        isPj: planDetail.isPj,
        hasOrthodontics: isShowingOrthodontics,
        numberOfLives: totalCount,
      });
    }
    setFirstEnter(false)
  }, [planDetail.isPj, isShowingOrthodontics, totalCount])

  const getByCodePlan = (code, countLifes) => {
    const codeToSearch = code || planDetail.code;
    getByCode(codeToSearch).then(plan => {
      setSelectedPlan(plan)
      setContractLink(() => mountContractLink(codeToSearch, countLifes))
    }).catch(console.error)
  }

  const debouncePlanCallback = useCallback(
    debounce(data => { getPlansByFilter(data) }, 800, { leading: true }),
    []
  )

  const getPlansByFilter = async ({ isPj, hasOrthodontics, numberOfLives }) => {
    try {
      
      const plan = await getPlanFilter(`?planType=${isPj ? 2 : 1}&hasOrthodontics=${hasOrthodontics}&NumberOfLives=${numberOfLives}`)
      getByCodePlan(plan.code, numberOfLives)
    } catch (e) {
      throw (e)
    }
  }

  const dependentsOrBeneficiaries = useMemo(() => (planDetail.isPj ? 'Beneficiários' : 'Dependentes'), [])

  const GracePeriodInfos = () => (
    plansType[0].selected ?
      <>
        <strong>Carência Zero!</strong>
        <p>Você começa a usar seu plano logo após aprovação do pagamento!</p>
      </>
      :
      <>
        <strong>Carência de 3 meses!</strong>
        <p>Você começa a usar seu plano logo após 3 meses da aprovação do primeiro pagamento!</p>
      </>
  )

  const [accredited, accreditedXs] = useImagesFluid({
    names: ['accredited.png', 'accredited-xs.png']
  });
  let bannerImage = accreditedXs;
  let bannerExtraStyles = {
    imgStyle: {
      objectFit: 'inherit',
      width: 'auto'
    },
    imgWrapperStyle: {
      width: 'auto',
      marginLeft: '48vw',
    },
    contentStyle: {}
  }

  if (isMobile) {
    bannerImage = accredited;
    bannerExtraStyles = {
      imgStyle: {
        objectFit: 'contain',
        width: '100vw'
      },
      imgWrapperStyle: {
        width: '100vw',
        marginTop: '238px'
      },
      contentStyle: {
        width: '100vw'
      }
    }
  }

  return (
    <Layout hideFixedFooter={true}>
      <Banner
        wrapperStyle={{ minHeight: 151 }}
        breadcrumbs={[
          { text: 'Home', to: '/' },
          { text: 'Detalhes do plano', to: '/detalhes-plano' }
        ]}
        title={
          <BannerTitle>
            {planDetail.name}
          </BannerTitle>
        }
      />
      <Styled.PlanDetailsWrapper>
        <Styled.Main>
          <section id="planDetailsDependents">
            <PlanDetailLane
              title={dependentsOrBeneficiaries}
              subTitle={`Quantos ${dependentsOrBeneficiaries.toLowerCase()} você quer?`}
              textAction={() => { }}
            >
              <Styled.HowToUseSection>
                <Styled.CounterButtom onClick={() => { setCount(prev => prev > rangeCount.min ? prev - 1 : rangeCount.min) }}>
                  <RemoveIcon />
                </Styled.CounterButtom>
                <Styled.CounterInput value={count} readOnly={true} />
                <Styled.CounterButtom onClick={() => { setCount(prev => prev < rangeCount.max ? prev + 1 : rangeCount.max ) }}>
                  <AddIcon />
                </Styled.CounterButtom>
              </Styled.HowToUseSection>
            </PlanDetailLane>
          </section>
          {!planDetail.isPj && <section id="planDetailsOrtodontics">
            <PlanDetailLane
              title={'Ortodontia'}
              subTitle={'Adicionar cobertura ortodontica'}
              textAction={() => { }}
            >
              <SwitchButton
                checked={isShowingOrthodontics}
                onChange={(o, val) => toggleOrthodontics(val)}
                name='checkOrthodonticsPlans'
                aria-label={`${isShowingOrthodontics ? 'Des' : 'S'
                  }elecionar planos com ortodontia`}
              />{' '}
            </PlanDetailLane>
          </section>}
          <section id="planDetailsPlanType">
            <PlanDetailLane
              title={'Tipo de Plano'}
              onlyTitle={true}
            >
              <Styled.PlanTypeSection>
                <Styled.TogglerSection>
                  <AnimatedToggler options={plansType} handleChange={setPlansType} />
                </Styled.TogglerSection>
                <Styled.GracePeriodPlanTypeSection>
                  <GracePeriodInfos />
                </Styled.GracePeriodPlanTypeSection>
              </Styled.PlanTypeSection>
            </PlanDetailLane>
          </section>
          <section id="planDetailsPaymentType">
            <PlanDetailLane
              title={'Formas de Pagamento'}
              onlyTitle={true}
            >
              <Styled.PaymentFormatList>
                {PF.map(({ name, icon: Icon, key }, i) => (
                  selectedPlan[key] &&
                  <Styled.PaymentFormatListItem key={i}>
                    <Styled.PaymentFormatListItemHeader>
                      <Icon /> <span>{name}</span>
                    </Styled.PaymentFormatListItemHeader>
                    <Styled.PaymentFormatListItemMain>
                      {selectedPlan && selectedPlan[key] && (
                        plansType[1].selected ?
                          <article>{formatToBRL(selectedPlan.recurrence.installment[0].value)} /mês </article>
                          :
                          (key === 'creditCard' ?
                            <>
                              <article>A vista - {formatToBRL(selectedPlan[key].discount.total.value)} </article>
                            </>
                            :
                            <article>1x {formatToBRL(selectedPlan[key].discount.total.value)}</article>
                          ))}
                    </Styled.PaymentFormatListItemMain>
                  </Styled.PaymentFormatListItem>
                ))}
              </Styled.PaymentFormatList>
            </PlanDetailLane>
          </section>
          <section id="planDetailsCoverage">
            <PlanDetailLane
              title={'Cobertura'}
              onlyTitle={true}
            >
              <section>
                <PlanProceduresList hasOrthodontics={isShowingOrthodontics} code={selectedPlan.code} size={'30%'} listStyle={{ padding: '12px 0' }} allProcedures={{ padding: 0, flexDirection: 'column', alignItems: 'flex-start' }} />
              </section>
            </PlanDetailLane>
          </section>
          <section id="planDetailsServices">
            <PlanDetailLane
              title={'Serviços'}
              onlyTitle={true}
            >
              <Styled.ServicesSection>
                {DentalServices.map(({ title, description }) => (
                  <Styled.ServicesArticle key={title}>
                    <Styled.ServicesBullet>●</Styled.ServicesBullet>
                    <span>
                      &nbsp;
                    <span>{title}</span>
                      <p>{description}</p>
                    </span>
                  </Styled.ServicesArticle>
                ))}
              </Styled.ServicesSection>
            </PlanDetailLane>
          </section>
          <section id="planDetailsHowCanIUse">
            <PlanDetailLane
              title={'Como uso meu plano'}
              onlyTitle={true}
            >
              <section>
                {HowToUseList.map(({ title, description, step, icon: Icon }) => (
                  <Styled.HowToUseItem key={step}>
                    <Icon />
                    <span>{title}</span>
                    <p>{description}</p>
                    <Styled.StepBullet>{step}</Styled.StepBullet>
                  </Styled.HowToUseItem>
                ))}
              </section>
            </PlanDetailLane>
          </section>
          <section id="planDetailsFAQ">
            <PlanDetailLane
              title={'Perguntas frequentes'}
              onlyTitle={true}
            >
              <FaqSection onlyQuestions={true} style={{ marginTop: 24 }} />
            </PlanDetailLane>
          </section>
        </Styled.Main>
        <Styled.Aside>
          <Styled.PlanLabel>Plano Selecionado</Styled.PlanLabel>
          <Styled.PlanName>{selectedPlan.name}</Styled.PlanName>
          <Styled.GracePeriod hasGracePeriod={plansType[1].selected}> {plansType[1].selected ? 'Carência: 3 meses' : 'Carência Zero'}</Styled.GracePeriod>
          <Styled.TotalLifes>Total de vidas: <strong>{String(totalCount).padStart(2, '0')}</strong>
            <p>Titular + Dependentes</p>
          </Styled.TotalLifes>
          <Styled.TotalValue>
            Total
          <Styled.Price>{parseToBRL(totalValue)}</Styled.Price>
            <p>por mês</p>
            {(contractLink || 1) && <Button
              component='a'
              variant='contained'
              color='primary'
              style={{ padding: '19px 50px', fontSize: '1rem', marginTop: 31, borderRadius: 50 }}
              id={`planDetail${selectedPlan.code}ContractButton`}
              rel='noopener noreferrer'
              target='_self'
              href={contractLink}
            >
              Contratar Plano
            </Button>
            }
          </Styled.TotalValue>
        </Styled.Aside>
      </Styled.PlanDetailsWrapper>
    </Layout>
  );
};

export default memo(PlanDetailsTemplate);
